import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import type { AiChatRepository } from '@/domain/repositories/ai-chat-repository.js';
import { ConfirmDialog, ErrorState, LoadingState, ReasonPromptDialog } from '@/presentation/components/index.js';
import type { DevelopmentController } from '@/presentation/controllers/development-controller.js';
import type { DevelopmentDetailController } from '@/presentation/controllers/development-detail-controller.js';
import type { ProjectController } from '@/presentation/controllers/project-controller.js';
import type { ReasonController } from '@/presentation/controllers/reason-controller.js';
import { useApp } from '@/presentation/hooks/use-app.js';
import { useAsyncAction } from '@/presentation/hooks/use-async-action.js';
import { useDevelopmentDetail } from '@/presentation/hooks/use-development-detail.js';
import { useDevelopmentEditOptions } from '@/presentation/hooks/use-development-edit-options.js';
import { useEfUploadStatus } from '@/presentation/hooks/use-ef-upload-status.js';
import { useEstimateForm } from '@/presentation/hooks/use-estimate-form.js';
import { useProjectMembers } from '@/presentation/hooks/use-project-members.js';
import { useReasonOptions } from '@/presentation/hooks/use-reason-options.js';
import { useSolicitationActions } from '@/presentation/hooks/use-solicitation-actions.js';
import { useTechLeadAssignment } from '@/presentation/hooks/use-tech-lead-assignment.js';
import { AuditTab } from '@/presentation/pages/developments/audit/audit-tab.js';
import { AssistedJiraLinkSection } from '@/presentation/pages/developments/development-detail/components/assisted-jira-link-section.js';
import { DevelopmentDetailHeader } from '@/presentation/pages/developments/development-detail/components/development-detail-header.js';
import { DevelopmentDetailTabs } from '@/presentation/pages/developments/development-detail/components/development-detail-tabs.js';
import { EditDevelopmentDialog } from '@/presentation/pages/developments/development-detail/components/dialogs/edit-development-dialog.js';
import { ReclassifySolicitationDialog } from '@/presentation/pages/developments/development-detail/components/dialogs/reclassify-solicitation-dialog.js';
import { JiraRetryBanner } from '@/presentation/pages/developments/development-detail/components/jira-retry-banner.js';
import { JiraStoriesStatus } from '@/presentation/pages/developments/development-detail/components/jira-stories-status.js';
import { JiraSyncFailureBanner } from '@/presentation/pages/developments/development-detail/components/jira-sync-failure-banner.js';
import { EstimateTab } from '@/presentation/pages/developments/development-detail/components/tabs/estimate-tab.js';
import { SolicitationsTab } from '@/presentation/pages/developments/development-detail/components/tabs/solicitations-tab.js';
import { SummaryTab } from '@/presentation/pages/developments/development-detail/components/tabs/summary-tab.js';
import { getVisibleTabs, type TabIndex } from '@/presentation/pages/developments/development-detail/utils/get-visible-tabs.js';
import { readJiraCreateWarning } from '@/presentation/pages/developments/development-detail/utils/read-jira-create-warning.js';
import { resolveDetailPermissions } from '@/presentation/pages/developments/development-detail/utils/resolve-detail-permissions.js';
import { resolveEditMode } from '@/presentation/pages/developments/development-detail/utils/resolve-edit-mode.js';
import { resolveLatestAnalysis } from '@/presentation/pages/developments/development-detail/utils/resolve-latest-analysis.js';
import { resolveTabVisibility } from '@/presentation/pages/developments/development-detail/utils/resolve-tab-visibility.js';
import { DevelopmentTab } from '@/presentation/pages/developments/development-tab.js';
import { DocumentsTab } from '@/presentation/pages/developments/documents/documents-tab.js';
import { EfIaTab } from '@/presentation/pages/developments/ef-ia-tab.js';
import { NewSolicitationDialog, type NewSolicitationPayload } from '@/presentation/pages/developments/solicitations/new-solicitation-dialog.js';
import { ResolveSolicitationDialog } from '@/presentation/pages/developments/solicitations/resolve-solicitation-dialog.js';
import { jiraRetryLabel } from '@/presentation/pages/developments/utils/jira-sync-pending.js';
import { ValidationTab } from '@/presentation/pages/developments/validation/validation-tab.js';
import { ROUTES } from '@/shared/constants/routes.js';

interface Props {
    devController: DevelopmentController;
    detailController: DevelopmentDetailController;
    reasonController: ReasonController;
    projectController: ProjectController;
    aiChatRepository: AiChatRepository;
}

export function DevelopmentDetailPage({ devController, detailController, reasonController, projectController, aiChatRepository }: Props) {
    const { translate, useAuthStore } = useApp();
    const user = useAuthStore((s) => s.user);
    const navigate = useNavigate();
    const location = useLocation();
    const jiraCreateWarning = readJiraCreateWarning(location.state);
    const {
        canApproveEstimate,
        canReturnEstimateToAnalysis,
        canSubmitEstimate,
        canAssignTechLead,
        canViewSuggestedEstimates,
        canViewDevelopmentTab,
        canEditDevelopers,
        canRegisterStart,
        isCf,
        isUc,
        isGpc,
        canFinalizeDev,
        canManageSolicitations,
        canResolveSolicitation,
        canEvaluateSolicitation,
        canDeleteDev,
        canCancelDev,
        canReactivateDev,
        canImpedeDev,
        canResumeDev,
        canLinkDev,
        canRetryJira
    } = resolveDetailPermissions(user);

    const [docUploadPending, setDocUploadPending] = useState(false);
    const [finishing, setFinishing] = useState(false);
    const [cutoverToggling, setCutoverToggling] = useState(false);
    const [validationPending, setValidationPending] = useState(false);
    const [actionError, setActionError] = useState<string | null>(null);
    const [jiraRetrying, setJiraRetrying] = useState(false);

    const runAction = async (action: () => Promise<unknown>, setPending: (pending: boolean) => void) => {
        setActionError(null);
        setPending(true);
        try {
            const result = await action();
            if (result === false) {
                setActionError(translate('errors.generic'));
            }
        } catch (error) {
            console.error(error);
            setActionError(translate('errors.generic'));
        } finally {
            setPending(false);
        }
    };

    const handleUploadDocument = (docType: string, file: File) => runAction(() => uploadDocument(docType, file), setDocUploadPending);

    const handleRegisterDevEnd = (endDate: string) => runAction(() => registerDevEnd(endDate), setFinishing);

    const handleToggleCutover = (value: boolean) => runAction(() => toggleCutover(value), setCutoverToggling);

    const handleDownloadDocument = async (docId: string) => {
        setActionError(null);
        const ok = await downloadDocument(docId);
        if (!ok) {
            setActionError(translate('errors.generic'));
        }
    };

    const handleDownloadEf = async (version?: number) => {
        setActionError(null);
        const ok = await downloadEf(version);
        if (!ok) {
            setActionError(translate('errors.generic'));
        }
    };

    const runValidationAction = (action: () => Promise<unknown>) => runAction(action, setValidationPending);
    const { id } = useParams<{ id: string }>();
    const {
        data,
        auditData,
        pbiDecomposition,
        cfEvidences,
        ucEvidences,
        isLoading,
        error,
        reload,
        analysisJustCompleted,
        clearAnalysisJustCompleted,
        uploadEf,
        deleteEf,
        downloadEf,
        downloadDocument,
        sendToDevelopment,
        uploadDocument,
        registerDevEnd,
        toggleCutover,
        approveDocumentation,
        rejectDocumentation,
        uploadValidationEvidence,
        releaseToUc,
        approveUcValidation,
        closeWithoutUc,
        retryPbiDecomposition,
        createEstimate,
        approveEstimate,
        rejectEstimate,
        returnEstimateToAnalysis,
        removeValidationEvidence,
        createSolicitation,
        approveSolicitation,
        rejectSolicitation,
        resolveSolicitation,
        reclassifySolicitation,
        listSolicitationEvidences,
        uploadSolicitationEvidences,
        removeSolicitationEvidence,
        downloadSolicitationEvidence,
        uploadingEvidences,
        assignTechLead,
        cancelDevelopment,
        reactivateDevelopment,
        impedeDevelopment,
        resumeDevelopment,
        deleteDevelopment,
        updateDevelopment,
        startDevelopment,
        setDevelopers
    } = useDevelopmentDetail(devController, detailController, id!);
    const [tab, setTab] = useState<TabIndex>(0);
    const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
    const [returnDialogOpen, setReturnDialogOpen] = useState(false);
    const [evidenceToRemove, setEvidenceToRemove] = useState<{ type: 'cf' | 'uc'; id: string } | null>(null);
    const [createSolDialogOpen, setCreateSolDialogOpen] = useState(false);
    const [rejectSolTarget, setRejectSolTarget] = useState<string | null>(null);
    const [reclassifyTarget, setReclassifyTarget] = useState<string | null>(null);
    const [resolveTarget, setResolveTarget] = useState<string | null>(null);
    const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [reactivateConfirmOpen, setReactivateConfirmOpen] = useState(false);
    const [impedeDialogOpen, setImpedeDialogOpen] = useState(false);
    const [resumeConfirmOpen, setResumeConfirmOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [editError, setEditError] = useState<string | null>(null);

    const rejectReasons = useReasonOptions(reasonController, 'REJEICAO_ESTIMATIVA', canApproveEstimate);
    const returnReasons = useReasonOptions(reasonController, 'DEVOLUCAO_EF', canReturnEstimateToAnalysis);
    const effortIncreaseReasons = useReasonOptions(reasonController, 'AUMENTO_ESFORCO', canSubmitEstimate);
    const rejectSolicitationReasons = useReasonOptions(reasonController, 'REJEICAO_SOLICITACAO', canEvaluateSolicitation);
    const cancelReasons = useReasonOptions(reasonController, 'CANCELAMENTO', canCancelDev);
    const impedimentReasons = useReasonOptions(reasonController, 'IMPEDIMENTO', canImpedeDev);
    const canReviewDocumentation = isCf && data?.status === 'AGUARDANDO_REVISAO_DOCUMENTACAO';
    const reviewReasons = useReasonOptions(reasonController, 'REJEICAO_DOCUMENTACAO', canReviewDocumentation);

    const techLeads = useProjectMembers(projectController, data?.projectId, 'LT', canAssignTechLead);
    // A lista de candidatos alimenta o formulário de início (canRegisterStart) e a edição do time do gap
    // (canEditDevelopers) — ambas restritas a LT/GPA; nenhuma flag sozinha cobre os dois
    // consumidores da aba Desenvolvimento.
    const projectDevs = useProjectMembers(projectController, data?.projectId, 'DEV', canRegisterStart || canEditDevelopers);
    const editOptions = useDevelopmentEditOptions(projectController, devController, updateDevelopment);
    const estimateForm = useEstimateForm({
        devId: data?.id,
        status: data?.status,
        functionalComplexity: data?.functionalComplexity,
        functionalHours: data?.functionalHours,
        liderTecnicoId: data?.liderTecnicoId,
        createEstimate
    });
    const techLeadAssignment = useTechLeadAssignment({ liderTecnicoId: data?.liderTecnicoId, assignTechLead, genericErrorMessage: translate('errors.generic') });
    const efUploadStatus = useEfUploadStatus({ currentEfVersion: data?.currentEfDocument?.version, uploadEf, errorMessage: translate('efIa.uploadFailed') });
    const solicitationActions = useSolicitationActions({
        createSolicitation,
        uploadSolicitationEvidences,
        approveSolicitation,
        resolveSolicitation,
        reload,
        labels: {
            attachmentUploadFailed: (files) => translate('solicitations.attachmentUploadFailed', { files }),
            resolveUploadFailed: (files) => translate('solicitations.resolveUploadFailed', { files }),
            resolveFailed: translate('solicitations.resolveFailed'),
            createFailed: translate('solicitations.createFailed')
        }
    });
    const deleteAction = useAsyncAction(deleteDevelopment);
    const reactivateAction = useAsyncAction(reactivateDevelopment);
    const resumeAction = useAsyncAction(resumeDevelopment);
    const removeEvidenceAction = useAsyncAction(removeValidationEvidence);
    const approveEstimateAction = useAsyncAction(approveEstimate);

    if (isLoading) {
        return <LoadingState />;
    }
    if (error) {
        return <ErrorState message={error} onRetry={reload} />;
    }
    if (!data) {
        return null;
    }

    const dev = data;
    const latestAnalysis = resolveLatestAnalysis(dev.aiAnalyses);

    const visibleTabs = getVisibleTabs(dev.status, canViewDevelopmentTab);
    const {
        canEditDev,
        canLinkDevNow,
        fullEditStatus,
        showEstimateForm,
        functionalEstimateIsComplete,
        showTechLeadCard,
        canEditDevelopersNow,
        hasTechLead,
        showReturnToAnalysis,
        showFactoryEstimateForm
    } = resolveTabVisibility({ dev, isCf, canAssignTechLead, canEditDevelopers, canReturnEstimateToAnalysis, canSubmitEstimate, canLinkDev });
    // O CF (canEditDev) tem prioridade: um administrador global cai nos dois flags ao mesmo tempo e deve
    // ver o formulário completo, não o modo restrito ao vínculo.
    const canOpenEditDialog = canEditDev || canLinkDevNow;
    const linkOnlyEdit = !canEditDev && canLinkDevNow;

    const handleOpenEdit = () => {
        editOptions.open(dev, linkOnlyEdit);
        setEditError(null);
        setEditOpen(true);
    };

    const handleCloseEdit = () => {
        // Backdrop e ESC chamam `onClose` mesmo com o botão Cancelar desabilitado: fechar durante o salvamento
        // esconderia o erro do save, que só tem onde aparecer dentro do diálogo.
        if (editOptions.saving) {
            return;
        }
        setEditError(null);
        setEditOpen(false);
    };

    const handleRetryJira = async () => {
        setJiraRetrying(true);
        try {
            const result = await detailController.retryJira(dev.id);
            const failed = result.fold(
                (error) => error.message,
                () => null
            );
            if (failed) {
                setActionError(failed);
                return;
            }
            await reload();
        } catch (error) {
            console.error(error);
            setActionError(translate('errors.generic'));
        } finally {
            setJiraRetrying(false);
        }
    };

    const handleSaveEdit = async () => {
        // O diálogo é modal: o erro precisa aparecer dentro dele, senão o `Alert` da página fica escondido atrás.
        const saveError = await editOptions.save(resolveEditMode(linkOnlyEdit, fullEditStatus));
        setEditError(saveError);
        if (!saveError) {
            setEditOpen(false);
        }
    };

    const handleConfirmDelete = async () => {
        const success = await deleteAction.run();
        setDeleteConfirmOpen(false);
        if (success) {
            navigate(ROUTES.DEVELOPMENTS);
        }
    };

    const handleConfirmReactivate = async () => {
        await reactivateAction.run();
        setReactivateConfirmOpen(false);
    };

    const handleConfirmRemoveEvidence = async () => {
        if (!evidenceToRemove) {
            return;
        }
        await removeEvidenceAction.run(evidenceToRemove.type, evidenceToRemove.id);
        setEvidenceToRemove(null);
    };

    const handleCreateSolicitation = async (payload: NewSolicitationPayload) => {
        // Em falha o diálogo continua aberto com o erro e com o que o usuário digitou.
        const created = await solicitationActions.create(payload);
        if (created) {
            setCreateSolDialogOpen(false);
        }
    };

    const handleCloseCreateSolicitation = () => {
        solicitationActions.clearError();
        setCreateSolDialogOpen(false);
    };

    const handleConfirmResolveSolicitation = async (notes: string, files: File[]): Promise<boolean> => {
        if (!resolveTarget) {
            return false;
        }
        const resolved = await solicitationActions.resolve(resolveTarget, notes, files);
        if (resolved) {
            setResolveTarget(null);
        }
        return resolved;
    };

    const currentTab: TabIndex = visibleTabs.includes(tab) ? tab : (visibleTabs[0] ?? 0);
    const isFullHeightTab = currentTab === 1;

    return (
        <Box
            sx={{
                p: 3,
                ...(isFullHeightTab && {
                    px: 3,
                    py: 1.5,
                    height: 'calc(100dvh - 56px)',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                })
            }}
        >
            <DevelopmentDetailHeader
                displayId={dev.displayId}
                status={dev.status}
                title={dev.title}
                jiraEpicKey={dev.jiraEpicKey}
                jiraEpicUrl={dev.jiraEpicUrl}
                jiraEpicStatusName={dev.jiraEpicStatusName}
                epicSync={dev.jiraEpicSync}
                clientAbbreviation={dev.clientAbbreviation}
                projectAbbreviation={dev.projectAbbreviation}
                isFullHeightTab={isFullHeightTab}
                showReturnToAnalysis={showReturnToAnalysis}
                canDeleteDev={canDeleteDev}
                canCancelDev={canCancelDev}
                canReactivateDev={canReactivateDev}
                canImpedeDev={canImpedeDev}
                canResumeDev={canResumeDev}
                onBack={() => navigate(ROUTES.DEVELOPMENTS)}
                onReturnToAnalysis={() => setReturnDialogOpen(true)}
                onDelete={() => setDeleteConfirmOpen(true)}
                onCancel={() => setCancelDialogOpen(true)}
                onReactivate={() => setReactivateConfirmOpen(true)}
                onImpede={() => setImpedeDialogOpen(true)}
                onResume={() => setResumeConfirmOpen(true)}
            />
            <JiraStoriesStatus
                status={dev.jiraStoriesSync.status}
                generatingLabel={translate('devDetail.jiraStoriesGenerating')}
                creatingLabel={translate('devDetail.jiraStoriesCreating', { created: dev.jiraStoriesSync.created, total: dev.jiraStoriesSync.total })}
                waitingLabel={translate('devDetail.jiraStoriesWaiting')}
                doneLabel={translate('devDetail.jiraStoriesDone')}
                retryLabel={jiraRetryLabel(translate, dev.jiraStoriesSync.nextAttemptAt)}
                epicUrl={dev.jiraEpicUrl}
            />
            <JiraRetryBanner message={jiraCreateWarning ?? solicitationActions.jiraWarning} retrying={false} />
            <JiraSyncFailureBanner
                lastError={dev.jiraLastError}
                epicStatus={dev.jiraEpicSync.status}
                storiesStatus={dev.jiraStoriesSync.status}
                solicitationStatuses={dev.solicitations.map((solicitation) => solicitation.jiraSync?.status ?? 'NONE')}
                retryLabel={translate('devDetail.jiraRetry')}
                onRetry={canRetryJira ? () => void handleRetryJira() : undefined}
                retrying={jiraRetrying}
            />
            <AssistedJiraLinkSection
                developmentId={dev.id}
                jiraEpicKey={dev.jiraEpicKey}
                projectHasJira={dev.jiraEpicSync.status !== 'NONE'}
                epicCreateOpen={dev.jiraEpicCreateOpen}
                canLink={isCf}
                controller={detailController}
                onLinked={reload}
            />
            <DevelopmentDetailTabs currentTab={currentTab} visibleTabs={visibleTabs} isFullHeightTab={isFullHeightTab} onChange={(v) => setTab(v)} />

            {actionError && (
                <Alert severity="error" onClose={() => setActionError(null)} sx={{ mb: 2 }}>
                    {actionError}
                </Alert>
            )}

            {currentTab === 0 && <SummaryTab dev={dev} canOpenEditDialog={canOpenEditDialog} onEdit={handleOpenEdit} />}

            {currentTab === 1 && (
                <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                    <EfIaTab
                        data={dev}
                        pbiDecomposition={pbiDecomposition}
                        isCf={isCf}
                        analysisJustCompleted={analysisJustCompleted}
                        onClearAnalysisJustCompleted={clearAnalysisJustCompleted}
                        efUploadSuccessVersion={efUploadStatus.successVersion}
                        onClearEfUploadSuccess={() => efUploadStatus.clearSuccess()}
                        efUploadError={efUploadStatus.error}
                        onClearEfUploadError={() => efUploadStatus.clearError()}
                        onUploadEf={efUploadStatus.upload}
                        onDeleteEf={deleteEf}
                        onDownloadEf={handleDownloadEf}
                        onSendToDevelopment={sendToDevelopment}
                        onRetryPbiDecomposition={retryPbiDecomposition}
                        aiChatRepository={aiChatRepository}
                        onAnalysisCommitted={reload}
                    />
                </Box>
            )}

            {currentTab === 2 && (
                <EstimateTab
                    dev={dev}
                    showTechLeadCard={showTechLeadCard}
                    techLeads={techLeads}
                    selectedTechLeadId={techLeadAssignment.selectedTechLeadId}
                    onSelectTechLead={techLeadAssignment.selectTechLead}
                    assigningTechLead={techLeadAssignment.assigning}
                    assignTechLeadError={techLeadAssignment.error}
                    onAssignTechLead={() => techLeadAssignment.assign()}
                    hasTechLead={hasTechLead}
                    canViewSuggestedEstimates={canViewSuggestedEstimates}
                    latestAnalysis={latestAnalysis}
                    showEstimateForm={showEstimateForm}
                    canApproveEstimate={canApproveEstimate}
                    approvingEstimate={approveEstimateAction.pending}
                    onRejectEstimate={() => setRejectDialogOpen(true)}
                    onApproveEstimate={() => approveEstimateAction.run()}
                    showFactoryEstimateForm={showFactoryEstimateForm}
                    functionalEstimateIsComplete={functionalEstimateIsComplete}
                    estimateForm={estimateForm.form}
                    onEstimateFieldChange={estimateForm.setField}
                    estimateIsSuperior={estimateForm.isSuperior}
                    effortIncreaseReasons={effortIncreaseReasons}
                    submittingEstimate={estimateForm.submitting}
                    onSubmitEstimate={() => estimateForm.submit()}
                />
            )}

            {currentTab === 7 && (
                <DevelopmentTab
                    status={dev.status}
                    developers={dev.developers}
                    projectDevs={projectDevs}
                    canRegisterStart={canRegisterStart}
                    canEditDevelopers={canEditDevelopersNow}
                    onRegisterStart={startDevelopment}
                    onSaveDevelopers={setDevelopers}
                    translate={translate}
                />
            )}

            {currentTab === 3 && (
                <DocumentsTab
                    status={dev.status}
                    documents={data.documents}
                    canFinalizeDev={canFinalizeDev}
                    uploading={docUploadPending}
                    startDate={dev.startDate}
                    finishing={finishing}
                    cutoverNotRequired={data.cutoverNotRequired}
                    cutoverToggling={cutoverToggling}
                    onToggleCutover={handleToggleCutover}
                    onUpload={handleUploadDocument}
                    onRegisterDevEnd={handleRegisterDevEnd}
                    onDownloadDocument={handleDownloadDocument}
                    translate={translate}
                />
            )}

            {currentTab === 4 && (
                <ValidationTab
                    status={dev.status}
                    isCf={isCf}
                    isUc={isUc}
                    isGpc={isGpc}
                    ucAssigned={Boolean(dev.usuarioChaveId)}
                    cutoverNotRequired={data.cutoverNotRequired}
                    devDocuments={data.documents}
                    cfEvidences={cfEvidences}
                    ucEvidences={ucEvidences}
                    reviewReasons={reviewReasons}
                    submitting={validationPending}
                    onApproveDocumentation={() => runValidationAction(() => approveDocumentation())}
                    onRejectDocumentation={(reasonId, notes) => runValidationAction(() => rejectDocumentation(reasonId, notes))}
                    onUploadEvidence={(file, type) => runValidationAction(() => uploadValidationEvidence(file, type))}
                    onRemoveEvidence={(target) => setEvidenceToRemove(target)}
                    onDownloadDocument={handleDownloadDocument}
                    onReleaseToUc={() => {
                        if (!dev.usuarioChaveId) {
                            setActionError(translate('validation.releaseToUcNoUc'));
                            return;
                        }
                        runValidationAction(() => releaseToUc());
                    }}
                    onCloseWithoutUc={() => runValidationAction(() => closeWithoutUc())}
                    onApproveUc={() => runValidationAction(() => approveUcValidation())}
                    translate={translate}
                />
            )}

            {currentTab === 5 && (
                <SolicitationsTab
                    canManageSolicitations={canManageSolicitations}
                    canResolveSolicitation={canResolveSolicitation}
                    canEvaluateSolicitation={canEvaluateSolicitation}
                    solicitationError={solicitationActions.error}
                    solicitations={data.solicitations}
                    approvingSolicitationId={solicitationActions.approvingId}
                    onCreateNew={() => setCreateSolDialogOpen(true)}
                    onApprove={solicitationActions.approve}
                    onReject={(solicitationId) => setRejectSolTarget(solicitationId)}
                    onReclassify={(solicitationId) => setReclassifyTarget(solicitationId)}
                    onResolve={(solicitationId) => setResolveTarget(solicitationId)}
                    fetchEvidences={listSolicitationEvidences}
                    onDownloadEvidence={downloadSolicitationEvidence}
                    onRemoveEvidence={removeSolicitationEvidence}
                />
            )}

            {currentTab === 6 && <AuditTab entries={auditData?.entries ?? []} translate={translate} />}

            <ReasonPromptDialog
                open={rejectDialogOpen}
                title={translate('estimate.reject')}
                reasons={rejectReasons}
                reasonLabel={translate('estimate.rejectionReason')}
                confirm={{ label: translate('estimate.reject'), pendingLabel: translate('estimate.rejecting'), color: 'error' }}
                onClose={() => setRejectDialogOpen(false)}
                onConfirm={(payload) => rejectEstimate(payload.reasonId)}
            />

            <ReasonPromptDialog
                open={returnDialogOpen}
                title={translate('estimate.returnToAnalysis')}
                reasons={returnReasons}
                reasonLabel={translate('estimate.returnReason')}
                notes={{ label: translate('estimate.returnNotes'), placeholder: translate('estimate.returnNotesPlaceholder'), rows: 3, required: true }}
                confirm={{ label: translate('estimate.returnToAnalysis'), pendingLabel: translate('estimate.wait') }}
                onClose={() => setReturnDialogOpen(false)}
                onConfirm={(payload) => returnEstimateToAnalysis(payload.reasonId, payload.notes)}
            />

            <ConfirmDialog
                open={!!evidenceToRemove}
                title={translate('validation.removeEvidenceConfirm')}
                confirmColor="error"
                loading={removeEvidenceAction.pending}
                onConfirm={() => handleConfirmRemoveEvidence()}
                onClose={() => setEvidenceToRemove(null)}
            />

            <NewSolicitationDialog
                open={createSolDialogOpen}
                submitting={solicitationActions.creating || uploadingEvidences}
                error={solicitationActions.error}
                onClose={handleCloseCreateSolicitation}
                onSubmit={handleCreateSolicitation}
                labels={{
                    newRequest: translate('solicitations.newRequest'),
                    requestType: translate('solicitations.requestType'),
                    requestTitle: translate('solicitations.requestTitle'),
                    requestDescription: translate('solicitations.requestDescription'),
                    requestSteps: translate('solicitations.requestSteps'),
                    cancel: translate('common.cancel'),
                    submit: translate('solicitations.submit'),
                    submittingRequest: translate('solicitations.submittingRequest'),
                    attachments: translate('solicitations.attachments'),
                    attachmentsHint: translate('solicitations.attachmentsHint'),
                    addFiles: translate('solicitations.addFiles'),
                    invalidFileFormat: (files) => translate('solicitations.invalidFileFormat', { files }),
                    fileTooLarge: (files) => translate('solicitations.fileTooLarge', { files }),
                    tooManyFiles: translate('solicitations.tooManyFiles')
                }}
                typeLabels={{ BUG: translate('requestTypeLabels.BUG'), AJUSTE_MINOR: translate('requestTypeLabels.AJUSTE_MINOR') }}
            />

            <ReasonPromptDialog
                open={!!rejectSolTarget}
                title={translate('solicitations.rejectRequest')}
                reasons={rejectSolicitationReasons}
                reasonLabel={translate('solicitations.rejectionReason')}
                notes={{ label: translate('solicitations.additionalNotes'), placeholder: translate('solicitations.rejectionDetailsPlaceholder'), rows: 2, required: false }}
                confirm={{ label: translate('solicitations.reject'), pendingLabel: translate('estimate.rejecting'), color: 'error' }}
                onClose={() => setRejectSolTarget(null)}
                onConfirm={(payload) => rejectSolicitation(rejectSolTarget!, payload.reasonId, payload.notes || undefined)}
            />

            <ReclassifySolicitationDialog
                open={!!reclassifyTarget}
                onClose={() => setReclassifyTarget(null)}
                onConfirm={(newType, notes) => reclassifySolicitation(reclassifyTarget!, newType, notes)}
            />

            <ResolveSolicitationDialog
                open={!!resolveTarget}
                resolving={solicitationActions.resolving}
                uploading={uploadingEvidences}
                onCancel={() => setResolveTarget(null)}
                onResolve={handleConfirmResolveSolicitation}
                labels={{
                    markResolved: translate('solicitations.markResolved'),
                    resolutionNotes: translate('solicitations.resolutionNotes'),
                    cancel: translate('common.cancel'),
                    resolve: translate('solicitations.resolve'),
                    attachments: translate('solicitations.attachments'),
                    attachmentsHint: translate('solicitations.attachmentsHint'),
                    addFiles: translate('solicitations.addFiles'),
                    invalidFileFormat: (files) => translate('solicitations.invalidFileFormat', { files }),
                    fileTooLarge: (files) => translate('solicitations.fileTooLarge', { files }),
                    tooManyFiles: translate('solicitations.tooManyFiles')
                }}
            />

            <ReasonPromptDialog
                open={cancelDialogOpen}
                title={translate('modals.cancelDevTitle')}
                reasons={cancelReasons}
                reasonLabel={translate('modals.cancelReasonLabel')}
                notes={{ label: translate('modals.justification'), placeholder: translate('modals.justificationPlaceholder'), rows: 3, required: true }}
                confirm={{ label: translate('modals.confirmCancel'), pendingLabel: translate('modals.cancelling'), color: 'error' }}
                onClose={() => setCancelDialogOpen(false)}
                onConfirm={(payload) => cancelDevelopment(payload.reasonId, payload.notes)}
            />

            <EditDevelopmentDialog
                open={editOpen}
                onClose={handleCloseEdit}
                onSave={() => handleSaveEdit()}
                saving={editOptions.saving}
                error={editError}
                fullEditStatus={fullEditStatus}
                linkOnly={linkOnlyEdit}
                form={editOptions.form}
                onFieldChange={editOptions.setField}
                businessProcesses={editOptions.businessProcesses}
                consultores={editOptions.consultores}
                keyUsers={editOptions.keyUsers}
                linkableDevs={editOptions.linkableDevs}
                jiraEpicKey={dev.jiraEpicKey}
                jiraEpicUrl={dev.jiraEpicUrl}
                epicSync={dev.jiraEpicSync}
            />

            <ConfirmDialog
                open={deleteConfirmOpen}
                title={translate('modals.deleteDevTitle')}
                message={translate('modals.deleteDevMsg')}
                confirmLabel={translate('modals.deletePermBtn')}
                confirmColor="error"
                loading={deleteAction.pending}
                onConfirm={() => handleConfirmDelete()}
                onClose={() => setDeleteConfirmOpen(false)}
            />

            <ConfirmDialog
                open={reactivateConfirmOpen}
                title={translate('modals.reactivateTitle')}
                message={translate('modals.reactivateMsg', { label: translate(`statusLabels.${dev.lastActiveStatus ?? 'EF_EM_ELABORACAO'}`) })}
                confirmLabel={translate('modals.reactivate')}
                loading={reactivateAction.pending}
                onConfirm={() => handleConfirmReactivate()}
                onClose={() => setReactivateConfirmOpen(false)}
            />

            <ReasonPromptDialog
                open={impedeDialogOpen}
                title={translate('devDetail.impedeDialogTitle')}
                reasons={impedimentReasons}
                reasonLabel={translate('devDetail.impedeReasonLabel')}
                notes={{ label: translate('devDetail.impedeDescriptionLabel'), placeholder: translate('devDetail.impedeDescriptionPlaceholder'), rows: 3, required: true }}
                confirm={{ label: translate('devDetail.impedeConfirmBtn'), pendingLabel: translate('devDetail.impedePendingLabel'), color: 'warning' }}
                onClose={() => setImpedeDialogOpen(false)}
                onConfirm={(payload) => impedeDevelopment(payload.reasonId, payload.notes)}
            />

            <ConfirmDialog
                open={resumeConfirmOpen}
                title={translate('devDetail.resumeConfirmTitle')}
                message={translate('devDetail.resumeConfirmMessage', { status: translate(`statusLabels.${dev.lastActiveStatus ?? 'EM_DESENVOLVIMENTO'}`) })}
                confirmLabel={translate('devDetail.resumeConfirmBtn')}
                loading={resumeAction.pending}
                onConfirm={async () => {
                    await resumeAction.run();
                    setResumeConfirmOpen(false);
                }}
                onClose={() => setResumeConfirmOpen(false)}
            />
        </Box>
    );
}
